import React, { useState, useEffect, useRef, useCallback } from 'react';
import './App.css';

// ─── VEHICLE DATA ────────────────────────────────────────────────────────────
const VEHICLES = {
  car:        { emoji: '🚗', name: 'Car',        color: '#FF6B6B' },
  truck:      { emoji: '🚛', name: 'Truck',       color: '#4ECDC4' },
  bus:        { emoji: '🚌', name: 'Bus',         color: '#FFE66D' },
  motorcycle: { emoji: '🏍️', name: 'Motorcycle',  color: '#A8E6CF' },
  ambulance:  { emoji: '🚑', name: 'Ambulance',   color: '#FF8B94' },
  fireEngine: { emoji: '🚒', name: 'Fire Engine', color: '#FF6348' },
  police:     { emoji: '🚓', name: 'Police Car',  color: '#74B9FF' },
  taxi:       { emoji: '🚕', name: 'Taxi',        color: '#FFEAA7' },
  rocket:     { emoji: '🚀', name: 'Rocket',      color: '#A29BFE' },
  airplane:   { emoji: '✈️', name: 'Airplane',    color: '#81ECEC' },
  helicopter: { emoji: '🚁', name: 'Helicopter',  color: '#FD79A8' },
  boat:       { emoji: '⛵', name: 'Sailboat',    color: '#55EFC4' },
};

const VEHICLE_KEYS = Object.keys(VEHICLES);

// ─── GAME CONFIG ─────────────────────────────────────────────────────────────
// Level 1: Left Click  | Level 2: Right Click | Level 3: Drag & Drop | Level 4: Mixed
// Each level has 4 stages with increasing difficulty
const GAME_CONFIG = [
  {
    level: 1,
    name: 'Left Click',
    icon: '🖱️',
    description: 'Click the vehicles with LEFT click!',
    color: '#FF6B6B',
    bg: 'linear-gradient(135deg, #FF6B6B22, #FF8E5322)',
    stages: [
      { stageNum: 1, targets: 5,  timeLimit: 30, speed: 0,    description: 'Click 5 vehicles!' },
      { stageNum: 2, targets: 8,  timeLimit: 25, speed: 0,    description: 'Click 8 vehicles!' },
      { stageNum: 3, targets: 10, timeLimit: 20, speed: 1500, description: 'Click moving vehicles!' },
      { stageNum: 4, targets: 12, timeLimit: 20, speed: 1000, description: 'Fast movers - click 12!' },
    ],
  },
  {
    level: 2,
    name: 'Right Click',
    icon: '🖱️',
    description: 'Click the vehicles with RIGHT click!',
    color: '#4ECDC4',
    bg: 'linear-gradient(135deg, #4ECDC422, #44CF9322)',
    stages: [
      { stageNum: 1, targets: 5,  timeLimit: 35, speed: 0,    description: 'Right-click 5 vehicles!' },
      { stageNum: 2, targets: 8,  timeLimit: 30, speed: 0,    description: 'Right-click 8 vehicles!' },
      { stageNum: 3, targets: 10, timeLimit: 25, speed: 1800, description: 'Right-click moving ones!' },
      { stageNum: 4, targets: 12, timeLimit: 22, speed: 1200, description: 'Fast! Right-click 12!' },
    ],
  },
  {
    level: 3,
    name: 'Drag & Drop',
    icon: '✋',
    description: 'Drag vehicles to their matching garage!',
    color: '#A8E6CF',
    bg: 'linear-gradient(135deg, #A8E6CF22, #88D8B022)',
    stages: [
      { stageNum: 1, targets: 3, timeLimit: 45, speed: 0, description: 'Drag 3 vehicles to garages!' },
      { stageNum: 2, targets: 4, timeLimit: 40, speed: 0, description: 'Drag 4 vehicles to garages!' },
      { stageNum: 3, targets: 5, timeLimit: 40, speed: 0, description: 'Drag 5 vehicles to garages!' },
      { stageNum: 4, targets: 6, timeLimit: 35, speed: 0, description: 'Drag 6 vehicles to garages!' },
    ],
  },
  {
    level: 4,
    name: 'Master Mix',
    icon: '⭐',
    description: 'Use ALL your skills! Mixed challenges!',
    color: '#A29BFE',
    bg: 'linear-gradient(135deg, #A29BFE22, #6C5CE722)',
    stages: [
      { stageNum: 1, targets: 6,  timeLimit: 30, speed: 0,    description: 'Left & right click mix!', mixed: 'click' },
      { stageNum: 2, targets: 8,  timeLimit: 28, speed: 1500, description: 'Click moving vehicles!',  mixed: 'click' },
      { stageNum: 3, targets: 4,  timeLimit: 40, speed: 0,    description: 'Click & drag mix!',       mixed: 'all' },
      { stageNum: 4, targets: 10, timeLimit: 35, speed: 1200, description: 'MASTER CHALLENGE!',       mixed: 'all' },
    ],
  },
];

// ─── UTILITIES ───────────────────────────────────────────────────────────────
const rand = (min, max) => Math.floor(Math.random() * (max - min + 1)) + min;
const randVehicle = () => VEHICLES[VEHICLE_KEYS[rand(0, VEHICLE_KEYS.length - 1)]];
const randPos = (maxW, maxH, size = 80) => ({
  x: rand(10, maxW - size - 10),
  y: rand(10, maxH - size - 10),
});

// ─── SOUND ENGINE ────────────────────────────────────────────────────────────
let _audioCtx = null;
function getAudioCtx() {
  if (!_audioCtx) {
    try { _audioCtx = new (window.AudioContext || window.webkitAudioContext)(); } catch(e) {}
  }
  return _audioCtx;
}
function playTone(freq, type, duration, gainVal, startDelay = 0) {
  const ctx = getAudioCtx();
  if (!ctx) return;
  try {
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.type = type;
    osc.frequency.setValueAtTime(freq, ctx.currentTime + startDelay);
    gain.gain.setValueAtTime(gainVal, ctx.currentTime + startDelay);
    gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + startDelay + duration);
    osc.start(ctx.currentTime + startDelay);
    osc.stop(ctx.currentTime + startDelay + duration);
  } catch(e) {}
}
const SFX = {
  hit:     () => { playTone(523, 'sine', 0.1, 0.3); playTone(784, 'sine', 0.15, 0.2, 0.05); },
  miss:    () => { playTone(220, 'sawtooth', 0.12, 0.25); playTone(150, 'sawtooth', 0.1, 0.2, 0.08); },
  combo:   (n) => { [523, 659, 784, 1047].slice(0, Math.min(n, 4)).forEach((f, i) => playTone(f, 'sine', 0.12, 0.25, i * 0.08)); },
  success: () => { [523, 659, 784, 1047, 1319].forEach((f, i) => playTone(f, 'sine', 0.15, 0.3, i * 0.1)); },
  drop:    () => { playTone(440, 'sine', 0.1, 0.3); playTone(880, 'sine', 0.12, 0.25, 0.06); },
};

// ─── SCREENS ─────────────────────────────────────────────────────────────────
const SCREEN = { WELCOME: 'welcome', MAP: 'map', STAGE: 'stage', RESULT: 'result', COMPLETE: 'complete' };

// ─── PARTICLE SYSTEM ─────────────────────────────────────────────────────────
function Particle({ x, y, emoji, onDone }) {
  useEffect(() => {
    const t = setTimeout(onDone, 800);
    return () => clearTimeout(t);
  }, [onDone]);

  return (
    <div className="particle" style={{ left: x, top: y }}>
      {emoji}
    </div>
  );
}

// ─── LEFT / RIGHT CLICK STAGE ────────────────────────────────────────────────
function ClickStage({ levelCfg, stageCfg, playerName, onComplete, soundEnabled, onToggleSound }) {
  const [vehicles, setVehicles] = useState([]);
  const [score, setScore] = useState(0);
  const [clicks, setClicks] = useState(0); // wrong clicks
  const [timeLeft, setTimeLeft] = useState(stageCfg.timeLimit);
  const [particles, setParticles] = useState([]);
  const [combo, setCombo] = useState(0);
  const [comboDisplay, setComboDisplay] = useState(null);
  const [speedMultiplier, setSpeedMultiplier] = useState(1);
  const speedMultiplierRef = useRef(1);
  const soundEnabledRef = useRef(soundEnabled);
  useEffect(() => { soundEnabledRef.current = soundEnabled; }, [soundEnabled]);
  const areaRef = useRef(null);
  const timerRef = useRef(null);
  const moveIntervalRef = useRef({});
  const pidRef = useRef(0);
  const comboRef = useRef(0);
  const targetRef = useRef(stageCfg.targets);

  const handleSpeedChange = (e) => {
    const val = parseFloat(e.target.value);
    setSpeedMultiplier(val);
    speedMultiplierRef.current = val;
  };

  // Determine which click type is "correct" for this stage
  const getRequiredClick = useCallback((vehicleId) => {
    if (levelCfg.level === 1) return 'left';
    if (levelCfg.level === 2) return 'right';
    if (levelCfg.level === 4) {
      // In mixed stages, each vehicle is randomly assigned
      return vehicleId % 2 === 0 ? 'left' : 'right';
    }
    return 'left';
  }, [levelCfg.level]);

  const spawnVehicle = useCallback(() => {
    if (!areaRef.current) return;
    const { clientWidth: W, clientHeight: H } = areaRef.current;
    const v = randVehicle();
    const pos = randPos(W, H, 80);
    const id = Date.now() + Math.random();
    const required = levelCfg.level === 4 ? (Math.random() > 0.5 ? 'left' : 'right') : (levelCfg.level === 1 ? 'left' : 'right');

    setVehicles(prev => [...prev, { id, ...v, x: pos.x, y: pos.y, required, bouncing: stageCfg.speed > 0 }]);

    // Start bouncing
    if (stageCfg.speed > 0) {
      let dx = (Math.random() > 0.5 ? 1 : -1) * (1.5 + Math.random());
      let dy = (Math.random() > 0.5 ? 1 : -1) * (1.5 + Math.random());
      let cx = pos.x, cy = pos.y;

      const interval = setInterval(() => {
        if (!areaRef.current) return;
        const { clientWidth: W2, clientHeight: H2 } = areaRef.current;
        cx += dx * speedMultiplierRef.current;
        cy += dy * speedMultiplierRef.current;
        if (cx <= 0 || cx >= W2 - 80) { dx *= -1; cx = Math.max(0, Math.min(W2 - 80, cx)); }
        if (cy <= 0 || cy >= H2 - 80) { dy *= -1; cy = Math.max(0, Math.min(H2 - 80, cy)); }
        setVehicles(prev => prev.map(vv => vv.id === id ? { ...vv, x: cx, y: cy } : vv));
      }, 16);
      moveIntervalRef.current[id] = interval;
    }
  }, [levelCfg.level, stageCfg.speed]);

  // Spawn initial vehicles
  useEffect(() => {
    const initialCount = Math.min(4, stageCfg.targets);
    for (let i = 0; i < initialCount; i++) {
      setTimeout(() => spawnVehicle(), i * 150);
    }
  }, [spawnVehicle, stageCfg.targets]);

  // Timer
  useEffect(() => {
    timerRef.current = setInterval(() => {
      setTimeLeft(t => {
        if (t <= 1) {
          clearInterval(timerRef.current);
          onComplete({ score: comboRef.current * 10 + score, success: false, clicks });
          return 0;
        }
        return t - 1;
      });
    }, 1000);
    return () => clearInterval(timerRef.current);
  }, []);

  const addParticle = (x, y, emoji) => {
    const id = ++pidRef.current;
    setParticles(p => [...p, { id, x, y, emoji }]);
  };

  const handleVehicleClick = useCallback((e, vehicle) => {
    e.preventDefault();
    e.stopPropagation();
    const isLeft = e.type === 'click';
    const isRight = e.type === 'contextmenu';
    const correct = (vehicle.required === 'left' && isLeft) || (vehicle.required === 'right' && isRight);

    // Clear bounce interval
    if (moveIntervalRef.current[vehicle.id]) {
      clearInterval(moveIntervalRef.current[vehicle.id]);
      delete moveIntervalRef.current[vehicle.id];
    }

    if (correct) {
      comboRef.current++;
      setCombo(c => c + 1);
      if (soundEnabledRef.current) SFX.hit();
      const pts = 10 + (comboRef.current > 2 ? comboRef.current * 5 : 0);
      setScore(s => {
        const newScore = s + pts;
        if (newScore >= stageCfg.targets * 10) {
          clearInterval(timerRef.current);
          if (soundEnabledRef.current) SFX.success();
          setTimeout(() => onComplete({ score: newScore, success: true, clicks }), 300);
        }
        return newScore;
      });
      addParticle(vehicle.x + 40, vehicle.y, comboRef.current > 2 ? '🔥' : '⭐');
      setVehicles(prev => prev.filter(v => v.id !== vehicle.id));
      targetRef.current--;
      if (targetRef.current > 0) setTimeout(() => spawnVehicle(), 200);
    } else {
      comboRef.current = 0;
      setCombo(0);
      if (soundEnabledRef.current) SFX.miss();
      setClicks(c => c + 1);
      addParticle(vehicle.x + 40, vehicle.y, '❌');
    }

    if (comboRef.current > 2) {
      if (soundEnabledRef.current) SFX.combo(comboRef.current);
      setComboDisplay(`${comboRef.current}x COMBO! 🔥`);
      setTimeout(() => setComboDisplay(null), 1000);
    }
  }, [stageCfg.targets, spawnVehicle, onComplete, clicks]);

  useEffect(() => {
    return () => Object.values(moveIntervalRef.current).forEach(clearInterval);
  }, []);

  const progress = Math.min(100, (score / (stageCfg.targets * 10)) * 100);

  return (
    <div className="stage-wrapper" style={{ background: levelCfg.bg }}>
      <div className="hud">
        <div className="hud-left">
          <span className="player-tag">👤 {playerName}</span>
        </div>
        <div className="hud-center">
          <div className="progress-bar"><div className="progress-fill" style={{ width: `${progress}%`, background: levelCfg.color }} /></div>
          <span className="score-display">⭐ {score} pts</span>
        </div>
        <div className="hud-right">
          <button className="sound-btn" onClick={onToggleSound} title={soundEnabled ? 'Mute sounds' : 'Unmute sounds'}>
            {soundEnabled ? '🔊' : '🔇'}
          </button>
          <span className={`timer ${timeLeft <= 5 ? 'timer-danger' : ''}`}>⏱ {timeLeft}s</span>
        </div>
      </div>

      <div className="stage-instruction">
        <span className="instruction-badge" style={{ background: levelCfg.color }}>
          {stageCfg.description}
        </span>
        {levelCfg.level === 4 && (
          <div className="mixed-hint">
            <span className="hint-left">⬅️ Left click blue vehicles</span>
            <span className="hint-right">Right click orange vehicles ➡️</span>
          </div>
        )}
        {stageCfg.speed > 0 && (
          <div className="speed-control">
            <label className="speed-label">🚀 Speed: {speedMultiplier.toFixed(2)}x</label>
            <input
              className="speed-slider"
              type="range"
              min="0.25"
              max="3"
              step="0.25"
              value={speedMultiplier}
              onChange={handleSpeedChange}
            />
          </div>
        )}
      </div>

      {comboDisplay && <div className="combo-popup">{comboDisplay}</div>}

      <div className="play-area" ref={areaRef} onContextMenu={e => e.preventDefault()}>
        {vehicles.map(v => (
          <div
            key={v.id}
            className="vehicle-target"
            style={{
              left: v.x,
              top: v.y,
              background: v.required === 'right' ? '#FF8E53' : v.color,
              borderColor: v.required === 'right' ? '#FF6B35' : v.color,
              boxShadow: `0 4px 20px ${v.color}66`,
            }}
            onClick={e => handleVehicleClick(e, v)}
            onContextMenu={e => handleVehicleClick(e, v)}
          >
            <span className="vehicle-emoji">{v.emoji}</span>
            {levelCfg.level === 4 && (
              <span className="click-indicator">{v.required === 'left' ? '👈' : '👉'}</span>
            )}
          </div>
        ))}
        {particles.map(p => (
          <Particle key={p.id} x={p.x} y={p.y} emoji={p.emoji} onDone={() => setParticles(prev => prev.filter(pp => pp.id !== p.id))} />
        ))}
      </div>
    </div>
  );
}

// ─── DRAG & DROP STAGE ───────────────────────────────────────────────────────
function DragDropStage({ levelCfg, stageCfg, playerName, onComplete, soundEnabled, onToggleSound }) {
  const [pairs, setPairs] = useState([]);
  const [score, setScore] = useState(0);
  const [timeLeft, setTimeLeft] = useState(stageCfg.timeLimit);
  const [particles, setParticles] = useState([]);
  const [dragging, setDragging] = useState(null);
  const [completed, setCompleted] = useState(0);
  const areaRef = useRef(null);
  const timerRef = useRef(null);
  const pidRef = useRef(0);
  const scoreRef = useRef(0);
  const completedRef = useRef(0);
  const soundEnabledRef = useRef(soundEnabled);
  useEffect(() => { soundEnabledRef.current = soundEnabled; }, [soundEnabled]);

  // Generate pairs: vehicles on left, garages on right
  useEffect(() => {
    const selected = [];
    const keys = [...VEHICLE_KEYS];
    for (let i = 0; i < stageCfg.targets; i++) {
      const idx = rand(0, keys.length - 1);
      selected.push({ key: keys[idx], ...VEHICLES[keys[idx]], id: i });
      keys.splice(idx, 1);
    }
    const W = window.innerWidth * 0.8;
    const H = window.innerHeight * 0.55;
    setPairs(selected.map((v, i) => ({
      ...v,
      src: { x: rand(20, W * 0.3), y: rand(20 + i * 80, 40 + i * 80) },
      garage: { x: rand(W * 0.65, W - 90), y: rand(20 + i * 80, 40 + i * 80) },
      dropped: false,
    })));
  }, [stageCfg.targets]);

  // Timer
  useEffect(() => {
    timerRef.current = setInterval(() => {
      setTimeLeft(t => {
        if (t <= 1) {
          clearInterval(timerRef.current);
          onComplete({ score: scoreRef.current, success: false });
          return 0;
        }
        return t - 1;
      });
    }, 1000);
    return () => clearInterval(timerRef.current);
  }, []);

  const handleDragStart = (e, pair) => {
    setDragging(pair.id);
    e.dataTransfer.setData('vehicleId', pair.id);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDrop = (e, pair) => {
    e.preventDefault();
    const vehicleId = parseInt(e.dataTransfer.getData('vehicleId'));
    if (vehicleId === pair.id && !pair.dropped) {
      if (soundEnabledRef.current) SFX.drop();
      const newScore = scoreRef.current + 20;
      scoreRef.current = newScore;
      completedRef.current++;
      setScore(newScore);
      setCompleted(completedRef.current);
      setPairs(prev => prev.map(p => p.id === pair.id ? { ...p, dropped: true } : p));
      const pid = ++pidRef.current;
      setParticles(p => [...p, { id: pid, x: pair.garage.x, y: pair.garage.y, emoji: '🎉' }]);
      setTimeout(() => setParticles(prev => prev.filter(pp => pp.id !== pid)), 800);

      if (completedRef.current >= stageCfg.targets) {
        clearInterval(timerRef.current);
        if (soundEnabledRef.current) SFX.success();
        setTimeout(() => onComplete({ score: newScore, success: true }), 500);
      }
    }
    setDragging(null);
  };

  const handleDragOver = (e) => { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; };

  const progress = (completed / stageCfg.targets) * 100;

  return (
    <div className="stage-wrapper" style={{ background: levelCfg.bg }}>
      <div className="hud">
        <div className="hud-left"><span className="player-tag">👤 {playerName}</span></div>
        <div className="hud-center">
          <div className="progress-bar"><div className="progress-fill" style={{ width: `${progress}%`, background: levelCfg.color }} /></div>
          <span className="score-display">⭐ {score} pts  ({completed}/{stageCfg.targets})</span>
        </div>
        <div className="hud-right">
          <button className="sound-btn" onClick={onToggleSound} title={soundEnabled ? 'Mute sounds' : 'Unmute sounds'}>
            {soundEnabled ? '🔊' : '🔇'}
          </button>
          <span className={`timer ${timeLeft <= 5 ? 'timer-danger' : ''}`}>⏱ {timeLeft}s</span>
        </div>
      </div>

      <div className="stage-instruction">
        <span className="instruction-badge" style={{ background: levelCfg.color }}>
          {stageCfg.description}
        </span>
        <div className="drag-hint">Drag each 🚗 vehicle to its matching 🏠 garage!</div>
      </div>

      <div className="drag-area" ref={areaRef}>
        {pairs.map(pair => (
          <React.Fragment key={pair.id}>
            {/* Draggable Vehicle */}
            {!pair.dropped && (
              <div
                className={`draggable-vehicle ${dragging === pair.id ? 'dragging' : ''}`}
                style={{ left: pair.src.x, top: pair.src.y, background: pair.color, borderColor: pair.color, boxShadow: `0 4px 20px ${pair.color}66` }}
                draggable
                onDragStart={e => handleDragStart(e, pair)}
                onDragEnd={() => setDragging(null)}
              >
                <span>{pair.emoji}</span>
                <span className="vehicle-name-small">{pair.name}</span>
              </div>
            )}
            {/* Garage Drop Target */}
            <div
              className={`garage ${pair.dropped ? 'garage-done' : ''}`}
              style={{ left: pair.garage.x, top: pair.garage.y, borderColor: pair.color }}
              onDrop={e => handleDrop(e, pair)}
              onDragOver={handleDragOver}
            >
              {pair.dropped ? (
                <span className="garage-done-inner">{pair.emoji}✅</span>
              ) : (
                <>
                  <span className="garage-emoji">{pair.emoji}</span>
                  <span className="garage-label">Drop here!</span>
                </>
              )}
            </div>
            {/* Dotted line hint */}
            {!pair.dropped && (
              <svg className="connector-line" style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', pointerEvents: 'none', zIndex: 0 }}>
                <line
                  x1={pair.src.x + 45} y1={pair.src.y + 40}
                  x2={pair.garage.x + 45} y2={pair.garage.y + 40}
                  stroke={pair.color} strokeWidth="2" strokeDasharray="6 4" opacity="0.3"
                />
              </svg>
            )}
          </React.Fragment>
        ))}
        {particles.map(p => (
          <Particle key={p.id} x={p.x} y={p.y} emoji={p.emoji} onDone={() => setParticles(prev => prev.filter(pp => pp.id !== p.id))} />
        ))}
      </div>
    </div>
  );
}

// ─── STAGE ROUTER ────────────────────────────────────────────────────────────
function StageRunner({ levelCfg, stageCfg, playerName, onComplete, soundEnabled, onToggleSound }) {
  if (levelCfg.level === 3) {
    return <DragDropStage levelCfg={levelCfg} stageCfg={stageCfg} playerName={playerName} onComplete={onComplete} soundEnabled={soundEnabled} onToggleSound={onToggleSound} />;
  }
  if (levelCfg.level === 4 && stageCfg.mixed === 'all') {
    return <ClickStage levelCfg={levelCfg} stageCfg={stageCfg} playerName={playerName} onComplete={onComplete} soundEnabled={soundEnabled} onToggleSound={onToggleSound} />;
  }
  return <ClickStage levelCfg={levelCfg} stageCfg={stageCfg} playerName={playerName} onComplete={onComplete} soundEnabled={soundEnabled} onToggleSound={onToggleSound} />;
}

// ─── WELCOME SCREEN ──────────────────────────────────────────────────────────
function WelcomeScreen({ onStart }) {
  const [name, setName] = useState('');
  const [shake, setShake] = useState(false);

  const handleStart = () => {
    if (!name.trim()) { setShake(true); setTimeout(() => setShake(false), 600); return; }
    onStart(name.trim());
  };

  return (
    <div className="welcome-screen">
      <div className="welcome-bg">
        {['🚗','🚛','🚌','🏍️','🚑','🚒','✈️','🚀','⛵','🚁'].map((e, i) => (
          <span key={i} className="bg-vehicle" style={{ left: `${rand(0, 95)}%`, top: `${rand(0, 90)}%`, animationDelay: `${i * 0.3}s`, fontSize: `${rand(24, 48)}px` }}>{e}</span>
        ))}
      </div>
      <div className="welcome-card">
        <div className="welcome-logo">
          <span className="logo-icon">🏁</span>
          <h1 className="logo-title">Vehicle<br /><span className="logo-accent">Clicker!</span></h1>
        </div>
        <p className="welcome-sub">Practice your mouse skills with fun vehicle adventures!</p>
        <div className="skill-badges">
          <span className="skill-badge" style={{ background: '#FF6B6B' }}>👈 Left Click</span>
          <span className="skill-badge" style={{ background: '#4ECDC4' }}>👉 Right Click</span>
          <span className="skill-badge" style={{ background: '#A8E6CF', color: '#333' }}>✋ Drag & Drop</span>
          <span className="skill-badge" style={{ background: '#A29BFE' }}>⭐ Mix Master</span>
        </div>
        <div className={`name-input-wrap ${shake ? 'shake' : ''}`}>
          <label className="name-label">What's your name, Driver? 🚗</label>
          <input
            className="name-input"
            type="text"
            placeholder="Enter your name..."
            value={name}
            maxLength={20}
            onChange={e => setName(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && handleStart()}
          />
        </div>
        <button className="start-btn" onClick={handleStart}>
          <span>🚦 START GAME!</span>
        </button>
      </div>
    </div>
  );
}

// ─── LEVEL MAP ───────────────────────────────────────────────────────────────
function LevelMap({ playerName, totalScore, levelProgress, onSelectLevel }) {
  return (
    <div className="map-screen">
      <div className="map-header">
        <div className="map-player">
          <span className="map-player-icon">👤</span>
          <div>
            <div className="map-player-name">{playerName}</div>
            <div className="map-player-score">⭐ Total Score: {totalScore}</div>
          </div>
        </div>
        <h2 className="map-title">🗺️ Choose Your Level</h2>
      </div>
      <div className="level-road">
        {GAME_CONFIG.map((lvl, i) => {
          const prog = levelProgress[i] || { stagesCompleted: 0, unlocked: i === 0 };
          const isUnlocked = prog.unlocked;
          const isDone = prog.stagesCompleted >= 4;
          return (
            <div key={i} className={`level-node ${isUnlocked ? 'unlocked' : 'locked'} ${isDone ? 'done' : ''}`}
              onClick={() => isUnlocked && onSelectLevel(i)}
            >
              <div className="level-node-icon" style={{ background: isUnlocked ? lvl.color : '#ccc' }}>
                {isDone ? '✅' : isUnlocked ? lvl.icon : '🔒'}
              </div>
              <div className="level-node-info">
                <div className="level-node-name">Level {lvl.level}: {lvl.name}</div>
                <div className="level-node-stages">
                  {Array.from({ length: 4 }).map((_, s) => (
                    <span key={s} className={`stage-dot ${s < prog.stagesCompleted ? 'done' : ''}`} style={{ background: s < prog.stagesCompleted ? lvl.color : '' }} />
                  ))}
                </div>
              </div>
              {i < GAME_CONFIG.length - 1 && <div className="road-segment" />}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── STAGE SELECT ────────────────────────────────────────────────────────────
function StageSelect({ levelCfg, levelProgress, onSelectStage, onBack }) {
  return (
    <div className="stage-select" style={{ background: levelCfg.bg }}>
      <button className="back-btn" onClick={onBack}>← Back</button>
      <div className="stage-select-header">
        <span style={{ fontSize: 48 }}>{levelCfg.icon}</span>
        <h2 style={{ color: levelCfg.color }}>Level {levelCfg.level}: {levelCfg.name}</h2>
        <p>{levelCfg.description}</p>
      </div>
      <div className="stages-grid">
        {levelCfg.stages.map((stage, i) => {
          const isUnlocked = i === 0 || levelProgress.stagesCompleted >= i;
          const isDone = levelProgress.stagesCompleted > i;
          return (
            <div
              key={i}
              className={`stage-card ${isUnlocked ? 'unlocked' : 'locked'} ${isDone ? 'done' : ''}`}
              style={{ borderColor: isUnlocked ? levelCfg.color : '#ddd' }}
              onClick={() => isUnlocked && onSelectStage(i)}
            >
              <div className="stage-card-num" style={{ background: isUnlocked ? levelCfg.color : '#ddd' }}>
                {isDone ? '✅' : isUnlocked ? stage.stageNum : '🔒'}
              </div>
              <div className="stage-card-info">
                <div className="stage-card-title">Stage {stage.stageNum}</div>
                <div className="stage-card-desc">{stage.description}</div>
                <div className="stage-card-meta">
                  <span>⏱ {stage.timeLimit}s</span>
                  <span>🎯 {stage.targets} targets</span>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── RESULT SCREEN ───────────────────────────────────────────────────────────
function ResultScreen({ success, score, playerName, levelCfg, stageNum, onNext, onRetry, onMap }) {
  return (
    <div className={`result-screen ${success ? 'success' : 'fail'}`}>
      <div className="result-card">
        <div className="result-emoji">{success ? '🏆' : '😅'}</div>
        <h2 className="result-title">{success ? 'Awesome!!' : 'Try Again!'}</h2>
        <p className="result-player">{playerName}</p>
        <div className="result-score">
          <span className="result-score-label">Score</span>
          <span className="result-score-value" style={{ color: levelCfg.color }}>{score}</span>
        </div>
        <p className="result-level">{levelCfg.name} — Stage {stageNum + 1}</p>
        <div className="result-buttons">
          {success && <button className="result-btn next-btn" onClick={onNext} style={{ background: levelCfg.color }}>Next Stage ➡️</button>}
          <button className="result-btn retry-btn" onClick={onRetry}>🔄 Retry</button>
          <button className="result-btn map-btn" onClick={onMap}>🗺️ Map</button>
        </div>
      </div>
    </div>
  );
}

// ─── COMPLETE SCREEN ─────────────────────────────────────────────────────────
function CompleteScreen({ playerName, totalScore, onRestart }) {
  return (
    <div className="complete-screen">
      <div className="complete-card">
        <div className="complete-trophy">🏆</div>
        <h1 className="complete-title">YOU WIN!</h1>
        <p className="complete-player">🎉 Congratulations, {playerName}! 🎉</p>
        <p className="complete-sub">You mastered all vehicle challenges!</p>
        <div className="complete-score">
          <span>Total Score</span>
          <span className="complete-score-val">⭐ {totalScore}</span>
        </div>
        <div className="complete-badges">
          <span>🖱️ Click Master</span>
          <span>✋ Drag Expert</span>
          <span>⭐ Mix Champion</span>
        </div>
        <button className="complete-btn" onClick={onRestart}>🚗 Play Again!</button>
      </div>
    </div>
  );
}

// ─── MAIN APP ─────────────────────────────────────────────────────────────────
export default function App() {
  const [screen, setScreen] = useState(SCREEN.WELCOME);
  const [playerName, setPlayerName] = useState('');
  const [totalScore, setTotalScore] = useState(0);
  const [levelProgress, setLevelProgress] = useState(
    GAME_CONFIG.map((_, i) => ({ stagesCompleted: 0, unlocked: i === 0 }))
  );
  const [currentLevel, setCurrentLevel] = useState(0);
  const [currentStage, setCurrentStage] = useState(0);
  const [lastResult, setLastResult] = useState(null);
  const [soundEnabled, setSoundEnabled] = useState(true);
  const handleToggleSound = () => setSoundEnabled(s => !s);

  const handleStart = (name) => {
    setPlayerName(name);
    setScreen(SCREEN.MAP);
  };

  const handleSelectLevel = (levelIdx) => {
    setCurrentLevel(levelIdx);
    setScreen('stageSelect');
  };

  const handleSelectStage = (stageIdx) => {
    setCurrentStage(stageIdx);
    setScreen(SCREEN.STAGE);
  };

  const handleStageComplete = ({ score, success }) => {
    setTotalScore(s => s + score);
    setLastResult({ score, success });
    setScreen(SCREEN.RESULT);
  };

  const handleNext = () => {
    const nextStage = currentStage + 1;
    if (nextStage < 4) {
      // Advance stage
      setLevelProgress(prev => prev.map((p, i) => i === currentLevel
        ? { ...p, stagesCompleted: Math.max(p.stagesCompleted, nextStage) }
        : p
      ));
      setCurrentStage(nextStage);
      setScreen(SCREEN.STAGE);
    } else {
      // Level complete - unlock next level
      setLevelProgress(prev => prev.map((p, i) => {
        if (i === currentLevel) return { ...p, stagesCompleted: 4 };
        if (i === currentLevel + 1) return { ...p, unlocked: true };
        return p;
      }));
      if (currentLevel >= 3) {
        setScreen(SCREEN.COMPLETE);
      } else {
        setScreen(SCREEN.MAP);
      }
    }
  };

  const handleRetry = () => setScreen(SCREEN.STAGE);
  const handleMap = () => setScreen(SCREEN.MAP);
  const handleRestart = () => {
    setScreen(SCREEN.WELCOME);
    setTotalScore(0);
    setLevelProgress(GAME_CONFIG.map((_, i) => ({ stagesCompleted: 0, unlocked: i === 0 })));
    setCurrentLevel(0);
    setCurrentStage(0);
  };

  const levelCfg = GAME_CONFIG[currentLevel];
  const stageCfg = levelCfg?.stages[currentStage];

  return (
    <div className="app" onContextMenu={e => e.preventDefault()}>
      {screen === SCREEN.WELCOME && <WelcomeScreen onStart={handleStart} />}
      {screen === SCREEN.MAP && (
        <LevelMap playerName={playerName} totalScore={totalScore} levelProgress={levelProgress} onSelectLevel={handleSelectLevel} />
      )}
      {screen === 'stageSelect' && (
        <StageSelect levelCfg={levelCfg} levelProgress={levelProgress[currentLevel]} onSelectStage={handleSelectStage} onBack={() => setScreen(SCREEN.MAP)} />
      )}
      {screen === SCREEN.STAGE && (
        <StageRunner key={`${currentLevel}-${currentStage}`} levelCfg={levelCfg} stageCfg={stageCfg} playerName={playerName} onComplete={handleStageComplete} soundEnabled={soundEnabled} onToggleSound={handleToggleSound} />
      )}
      {screen === SCREEN.RESULT && lastResult && (
        <ResultScreen success={lastResult.success} score={lastResult.score} playerName={playerName} levelCfg={levelCfg} stageNum={currentStage} onNext={handleNext} onRetry={handleRetry} onMap={handleMap} />
      )}
      {screen === SCREEN.COMPLETE && (
        <CompleteScreen playerName={playerName} totalScore={totalScore} onRestart={handleRestart} />
      )}
    </div>
  );
}
